import { Controller, useForm } from "react-hook-form";
import { Button, Form, Spinner } from "react-bootstrap";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "react-router-dom";

import { SiteLogo } from "src/assets";
import PasswordInput from "src/components/common/PasswordInput";
import { ResetPasswordSchema } from "src/validation/resetPassword.schema";
import { useResetPassword } from "src/hooks/useAuth";
import type { ResetPasswordFormType } from "src/types/resetPassword";



export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const { mutate: resetPassword, isPending } = useResetPassword();

  const { control, handleSubmit } = useForm<ResetPasswordFormType>({
    mode: "all",
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      confirmPassword: "",
      newPassword: "",
    },
  });

  const submitForm = (values: ResetPasswordFormType) => {
    resetPassword({
      token,
      new_password: values.newPassword,
    });
  };

  return (
    <div className="login-page d-flex flex-fill">
      <div className="col-form">
        <div className="logo mb-3">
          <img src={SiteLogo} alt="Site logo" />
        </div>
        <div>
          <h3 className="mb-2 text-purple">Create New Password!</h3>
          <p className="mb-3">Create new password to access your portal</p>
        </div>

        <Form onSubmit={handleSubmit(submitForm)}>
          <Controller
            name="newPassword"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Form.Group className="mb-3" controlId="newPassword">
                <Form.Label>New Password</Form.Label>
                <PasswordInput
                  id="newPassword"
                  {...field}
                  error={error?.message}
                  placeholder=""
                  disabled={isPending}
                />
              </Form.Group>
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Form.Group className="mb-4" controlId="confirmPassword">
                <Form.Label>Confirm Password</Form.Label>
                <PasswordInput
                  id="confirmPassword"
                  {...field}
                  error={error?.message}
                  placeholder=""
                  disabled={isPending}
                />
              </Form.Group>
            )}
          />

          <div className="my-5">
            <Button
              className="w-100"
              variant="purple"
              type="submit"
              disabled={isPending || !token}
              style={{ minHeight: "45px" }}
            >
              {isPending && (
                <Spinner
                  className="me-1"
                  animation="border"
                  size="sm"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              )}
              Reset Password
            </Button>
          </div>
        </Form>
        <p className="small text-center text-primary m-0">
          © 2026 Foresight Elusa Health All rights reserved
        </p>
      </div>
      <div className="col-bg">
        <div className="bg-text">
          <h2 className="mb-2">
            Smarter Insights.
            <br />
            Better Patient Outcomes.
          </h2>
          <p className="mb-2">
            Foresight transforms patient services data into actionable
            intelligence, empowering pharmaceutical manufacturers to optimize
            patient access, enhance program performance, and improve patient
            outcomes.
          </p>
        </div>
      </div>
    </div>
  );
}
